import { useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { byCategory, categories, emoji, glyph, isCustom, noteUsed, recent, search, setSkinTone, skinTone, toned } from "../emoji";
import { useLantern } from "../hooks";
import { navigate } from "../router";
import { Emoji } from "./Emoji";
import { useDismiss, usePlace } from "./ui";
import { t } from "../i18n";

const columns = 8;
const sectionNames = (): Record<string, string> => ({
  recent: t("Recently used"),
  custom: t("Custom"),
  people: t("Smileys and people"),
  nature: t("Animals and nature"),
  food: t("Food and drink"),
  travel: t("Travel and places"),
  activity: t("Activities"),
  objects: t("Objects"),
  symbols: t("Symbols"),
  flags: t("Flags"),
});

// EmojiPicker chooses an emoji by name: search, or browse by category. It
// opens beside the button that asked for it, above it if there is no room
// below.
export function EmojiPicker({ anchor, onPick, onClose }: { anchor: DOMRect; onPick: (name: string) => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const panel = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const used = recent();
  const custom = useLantern().emoji.map((e) => e.name).filter(isCustom);
  const names = sectionNames();

  const sections: [string, string[]][] = q.trim()
    ? [["results", search(q).map((e) => e.name)]]
    : [
        ...(used.length ? [["recent", used] as [string, string[]]] : []),
        ...(custom.length ? [["custom", custom] as [string, string[]]] : []),
        ...categories.map((c) => [c.id, byCategory.get(c.id)!] as [string, string[]]),
      ];
  const flat = sections.flatMap(([, list]) => list);
  const at = Math.min(active, Math.max(flat.length - 1, 0));

  // Close on a click outside, or Escape.
  useDismiss(panel, onClose);
  const style = usePlace(panel, anchor, { align: "end" });
  useLayoutEffect(() => {
    grid.current?.querySelector<HTMLElement>(`[data-i="${at}"]`)?.scrollIntoView({ block: "nearest" });
  }, [at]);

  const [tone, setTone] = useState(skinTone);
  function pick(name: string) {
    const named = toned(name, tone);
    noteUsed(named);
    onPick(named);
    onClose();
  }
  function onKey(e: KeyboardEvent) {
    const move: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns };
    if (move[e.key] !== undefined) {
      e.preventDefault();
      setActive(Math.max(0, Math.min(flat.length - 1, at + move[e.key])));
    } else if (e.key === "Enter" && flat[at]) {
      e.preventDefault();
      pick(flat[at]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  }

  let i = 0;
  return createPortal(
    <div ref={panel} className="emoji-picker" style={style} role="dialog" aria-label={t("Choose an emoji")} onKeyDown={onKey}>
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
        }}
        placeholder={t("Search emoji")}
        aria-label={t("Search emoji")}
        autoFocus
        spellCheck={false}
      />
      <div className="emoji-tones" role="radiogroup" aria-label={t("Skin tone")}>
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={tone === n}
            className={tone === n ? "on" : ""}
            aria-label={n === 1 ? t("Default skin tone") : t("Skin tone {n}", { n: n - 1 })}
            onClick={() => {
              setTone(n);
              setSkinTone(n);
            }}
          >
            {glyph(n === 1 ? "raised_hand" : `raised_hand::skin-tone-${n}`)}
          </button>
        ))}
      </div>
      {!q.trim() && (
        <nav className="emoji-tabs" aria-label={t("Categories")}>
          {[...(used.length ? [{ id: "recent", icon: "🕘" }] : []), ...(custom.length ? [{ id: "custom", icon: "✨" }] : []), ...categories].map((c) => (
            <button
              key={c.id}
              title={names[c.id]}
              aria-label={names[c.id]}
              onClick={() => grid.current?.querySelector(`[data-section="${c.id}"]`)?.scrollIntoView({ block: "start" })}
            >
              {c.icon}
            </button>
          ))}
        </nav>
      )}
      <div className="emoji-grid" ref={grid}>
        {sections.map(([id, list]) => (
          <section key={id} data-section={id}>
            {id !== "results" && <h4>{names[id]}</h4>}
            <div className="emoji-cells" role="listbox" aria-label={names[id] ?? t("Matches")}>
              {list.map((name) => {
                const n = i++;
                return (
                  <button
                    key={id + name}
                    data-i={n}
                    role="option"
                    aria-selected={n === at}
                    className={n === at ? "active" : ""}
                    title={`:${name}:`}
                    aria-label={name.replace(/[_-]/g, " ")}
                    onMouseMove={() => setActive(n)}
                    onClick={() => pick(name)}
                  >
                    <Emoji name={toned(name, tone)} />
                  </button>
                );
              })}
            </div>
          </section>
        ))}
        {q.trim() && flat.length === 0 && <p className="emoji-none">{t("No emoji match “{q}”.", { q: q.trim() })}</p>}
      </div>
      <p className="emoji-footer">
        {flat[at] ? (
          <>
            <span className="emoji-big">
              <Emoji name={flat[at]} />
            </span>{" "}
            :{flat[at]}:
          </>
        ) : (
          t("{n} emoji", { n: emoji.length + custom.length })
        )}
        <button
          className="link emoji-add"
          onClick={() => {
            onClose();
            navigate("/emoji");
          }}
        >
          {t("Add emoji")}
        </button>
      </p>
    </div>,
    document.body,
  );
}
